import { queue } from 'async';
import axios, { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { v4 } from 'uuid';
import { IConfiguration } from '../../../configuration/config.types';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { decodeImage } from '../../helpers/imageDecoder.helpers';
import { ACDLError, Failed, ForbiddenError, LockedUserError, OperationResult, UnauthorizedError, UploadSuccess } from '../../models/error.model';
import { IApiService } from '../../services/api.service.base';
import { IApiServiceImageUpload } from '../../services/api.service.imageUpload';
import { ICryptoService } from '../../services/crypto.service';
import { IAuthStore } from '../auth/auth.store';
import { generateThumbnail, isAllowedImageType, isNotDuplicatedExtension, stripExifTags, toMBytes } from '../datasets/helpers/image.helpers';
import { IUploadedImage } from '../datasets/uploadServiceBase';
import { AttachmentsUploadNotification, NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IAttachmentsStore } from './attachments.store';
import { IAttachmentUploadData, IAttachmentUploadStore, IWillAttachmentUploadExceedsLimitDto, UploadStatus } from './attachmentUploadStore';

export interface IAttachmentUploadService {
  startUpload(onAllUploadedCallback: () => void | Promise<void>): void;
  prepareFilesAsync(files: File[], parentId: string | undefined): Promise<void>;
  cancelUpload(attachmentId: string): Promise<void>;
  downloadAttachmentsAsync(allAttachments: boolean, attachmentIds: string[], parentId?: string): Promise<void>;
  data: IAttachmentUploadStore;
}

export namespace IAttachmentUploadService {
  export const $: interfaces.ServiceIdentifier<IAttachmentUploadService> = Symbol('ATTACHMENT_UPLOAD_SERVICE');
}

@injectable()
export class AttachmentUploadService implements IAttachmentUploadService {
  constructor(
    @inject(IAttachmentUploadStore.$) public readonly data: IAttachmentUploadStore,
    @inject(IAttachmentsStore.$) public readonly attachmentsStore: IAttachmentsStore,
    @inject(IApiServiceImageUpload.$) protected readonly apiServiceImageUpload: IApiService,
    @inject(INotificationsService.$) protected readonly notificationService: INotificationsService,
    @inject(IConfiguration.$) protected readonly configuration: IConfiguration,
    @inject(IAuthStore.$) protected readonly authStore: IAuthStore,
    @inject(ICryptoService.$) protected readonly cryptoService: ICryptoService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    makeObservable(this);
  }
  async downloadAttachmentsAsync(allAttachments: boolean, attachmentIds: string[], parentId?: string): Promise<void> {
    const workspaceId = this.currentWorkspaceStore.currentWorkspace.id;
    const result = await this.apiServiceImageUpload.postAsync<string | ACDLError>('/Attachments/generateDownloadAttachmentId', {
      workspaceId,
      allAttachments,
      parentId,
      attachmentIds,
    });

    if (result instanceof ACDLError) {
      if (result instanceof UnauthorizedError || result instanceof ForbiddenError || result instanceof LockedUserError) {
        throw result;
      }

      await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'generating_attachments_url_failed'));
      return;
    }

    this.attachmentsStore.clearSelectedAttachments();

    const downloadUrl = new URL(result, this.configuration.appConfig.imageUploadBaseUrl).toString();
    window.open(downloadUrl, '_self');
  }

  @action.bound
  public startUpload(onAllUploadedCallback: () => void): void {
    this.setupQueue(onAllUploadedCallback);
    for (const preparedFile of this.data.preparedFiles.filter((x) => x.status === UploadStatus.Valid)) {
      const id = v4();
      this.data.uploadQueue.push({ id, file: preparedFile.file, workspaceId: preparedFile.workspaceId, parentId: preparedFile.parentId }, () => {});
      this.data.progressData.push({ id, name: preparedFile.file.name, progress: 0, isSuccess: true });
    }
  }

  @action.bound
  public async prepareFilesAsync(files: File[], parentId: string | undefined): Promise<void> {
    const workspaceId = this.currentWorkspaceStore.currentWorkspace.id;

    const preparedFilesSize = this.data.preparedFiles.filter((x) => x.status === UploadStatus.Valid).reduce((sum, current) => sum + current.file.size, 0);
    const newFilesSize = files.reduce((sum, current) => sum + current.size, 0);

    this.data.willExceedsLimit = await this.willUploadExceedLimitsAsync(workspaceId, toMBytes(preparedFilesSize + newFilesSize));

    const duplicateNames = [
      ...(await this.getDuplicatedAttachmentNamesAsync(
        workspaceId,
        parentId,
        files.map((x) => x.name)
      )),
      ...this.data.preparedFiles.filter((x) => x.status === UploadStatus.Valid).map((x) => x.file.name),
    ];

    for (const file of files) {
      let status = UploadStatus.Valid;
      if (duplicateNames.includes(file.name)) {
        status = UploadStatus.DuplicatedName;
      } else if (!isNotDuplicatedExtension(file.name)) {
        status = UploadStatus.DuplicatedExtension;
      } else if (toMBytes(file.size) > 1024) {
        status = UploadStatus.InvalidSize;
      }
      this.data.preparedFiles.push({ file, status, workspaceId, parentId });
    }
  }

  @action.bound
  public async cancelUpload(attachmentId: string): Promise<void> {
    this.data.uploadQueue.remove((x) => x.data.id === attachmentId);

    const currentUploadIndex = this.data.currentUploads.findIndex((x) => x.id === attachmentId);
    if (currentUploadIndex !== -1) {
      this.data.currentUploads[currentUploadIndex].cancelToken.cancel();
      this.data.currentUploads.splice(currentUploadIndex, 1);
    }

    const progressIndex = this.data.progressData.findIndex((x) => x.id === attachmentId);

    await this.notificationService.push(
      new ToastNotification(
        NotificationLevel.INFO,
        { template: 'attachment_upload_canceled' },
        { template: 'upload_of_attachment_x_canceled', data: { fileName: this.data.progressData[progressIndex].name } }
      )
    );

    this.data.progressData.splice(progressIndex, 1);
  }

  // dumb inversify construct all services on start, so the configuration object is not initialized at that point
  private setupQueue = (onAllUploadedCallback: () => void): void => {
    if (!this.data.uploadQueue) {
      this.data.uploadQueue = queue((d, c) => {
        void this.uploadFileAsync(d, c);
      }, this.configuration.appConfig.concurrentAttachmentUploadsCount);
    }
    this.data.uploadQueue.drain(() => {
      void this.onAllItemsUploadedAsync(onAllUploadedCallback);
    });
  };

  @action.bound
  private updateProgress(id: string, progress: number): void {
    const progressData = this.data.progressData.find((x) => x.id === id);
    if (progressData) {
      progressData.progress = progress;
    }
  }

  private async uploadFileAsync(data: IAttachmentUploadData, onUploadItemFinished: () => void): Promise<void> {
    const result = await this.performUploadAsync(data);

    const progressData = this.data.progressData.find((x) => x.id === data.id);
    if (progressData) {
      progressData.isSuccess = result.isSuccess;
      progressData.progress = 100;
    }

    onUploadItemFinished();
  }

  private performUploadAsync = async (data: IAttachmentUploadData): Promise<OperationResult> => {
    try {
      const encrypt = this.cryptoService.hasKey(this.currentWorkspaceStore.currentWorkspace.id);

      const formData = new FormData();

      const params: Record<string, string | boolean | number | undefined> = {
        workspaceId: data.workspaceId,
        parentId: data.parentId,
        isEncrypted: encrypt,
        fileName: data.file.name,
        width: 0,
        height: 0,
      };

      if (isAllowedImageType(data.file.name)) {
        try {
          const strippedImage = await stripExifTags(data.file);
          const strippedImageBuffer = await strippedImage.arrayBuffer();
          formData.append(
            'file',
            encrypt ? await this.cryptoService.encrypt(this.currentWorkspaceStore.currentWorkspace.id, strippedImageBuffer) : strippedImage
          );
          const thumbnail = await generateThumbnail(strippedImage);
          formData.append(
            'thumbnail',
            encrypt ? await this.cryptoService.encrypt(this.currentWorkspaceStore.currentWorkspace.id, await thumbnail.arrayBuffer()) : thumbnail
          );
          const metadata = decodeImage(strippedImageBuffer);
          params.width = metadata.width;
          params.height = metadata.height;
        } catch (e) {
          await this.notificationService.push(
            new ToastNotification(
              NotificationLevel.ERROR,
              { template: 'x_is_not_an_image_file', data: { fileName: data.file.name } },
              { template: 'attachment_is_not_an_image_file' }
            )
          );
          throw e;
        }
      } else {
        formData.append(
          'file',
          encrypt ? await this.cryptoService.encrypt(this.currentWorkspaceStore.currentWorkspace.id, await data.file.arrayBuffer()) : data.file
        );
      }

      const cancelTokenSource = axios.CancelToken.source();
      this.data.currentUploads.push({ id: data.id, cancelToken: cancelTokenSource });

      const config: AxiosRequestConfig = {
        params,
        cancelToken: cancelTokenSource.token,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const progress = Math.max(Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? 100)) - 1, 0);
          this.updateProgress(data.id, progress);
        },
      };

      const url = '/attachments/addAttachment';

      const result = await this.apiServiceImageUpload.postAsync<IUploadedImage>(url, formData, config);
      if (result instanceof ACDLError) {
        return new Failed(result);
      }
      return new UploadSuccess(result.imageSize);
    } catch {
      return new Failed();
    } finally {
      const index = this.data.currentUploads.findIndex((x) => x.id === data.id);
      this.data.currentUploads.splice(index, 1);
    }
  };

  readFileAsync = async (file: File): Promise<ArrayBuffer | null> => {
    return new Promise<ArrayBuffer | null>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (): void => {
        resolve(reader.result as ArrayBuffer | null);
      };

      reader.onerror = reject;

      reader.readAsArrayBuffer(file);
    });
  };

  private async getDuplicatedAttachmentNamesAsync(workspaceId: string, parentId: string | undefined, fileNames: string[]): Promise<string[]> {
    const distinct = fileNames.filter((v, i, a) => a.indexOf(v) === i);

    const url = '/attachments/getDuplicatedFileNames';
    const result = await this.apiServiceImageUpload.postAsync<string[]>(url, {
      workspaceId,
      parentId,
      fileNames: distinct,
    });

    if (result instanceof ACDLError) {
      throw result;
    }

    return result;
  }

  private async willUploadExceedLimitsAsync(workspaceId: string, newFilesSize: number): Promise<IWillAttachmentUploadExceedsLimitDto> {
    const url = '/attachments/willAttachmentUploadExceedsLimit';
    const result = await this.apiServiceImageUpload.postAsync<IWillAttachmentUploadExceedsLimitDto>(url, {
      workspaceId,
      newFilesSize,
    });

    if (result instanceof ACDLError) {
      throw result;
    }

    return result;
  }

  @action.bound
  private async onAllItemsUploadedAsync(onAllUploadedCallback: () => void): Promise<void> {
    if (this.data.progressData.length > 0) {
      await this.notificationService.push(
        new AttachmentsUploadNotification(
          this.data.progressData.filter((x) => !x.isSuccess).map((x) => x.name),
          this.data.progressData.filter((x) => x.isSuccess).length
        )
      );

      this.data.progressData.replace([]);
      onAllUploadedCallback();
    }
  }
}
